import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import BoundImageModal from '../../components/boundImageModal';
import {Link, StyledPageCenterContainer, pxToEm} from '../../components/globalStyles';
import HexagonIconImage, {HexagonBaseBg} from '../../components/hexagonIconImage';
import HexagonLeaflet, {Direction as HexagonLeafletDirection} from '../../components/hexagonLeaflet';
import Layout from '../../components/layout/layout';
import PageViewHeadline from '../../components/pageViewHeadline';
import SeeItInActionButton from '../../components/seeItInActionButton';
import WhyBitmovinProductSection from '../../components/whyBitmovinProductSection';
import WrapperAppUl from '../../components/wrapperAppUl';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {Product, ProductToAppRoutePath, ProductToHexagonIconSrc, ProductToText} from '../../data/product';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import HybridMultiCloudSvg from '../../media/hexagonIconImage/icons/hybrid_multi_cloud.svg';
import StartDemoSvg from '../../media/hexagonIconImage/icons/start_demo.svg';
import OnPremOverviewPng from '../../media/onPremiseEncodingPageView/on_prem_overview.png';
import OnPremWorkflowPng from '../../media/onPremiseEncodingPageView/on_prem_workflow.png';
import RunsAnywherePng from '../../media/onPremiseEncodingPageView/runs_anywhere_v2.png';

import OnPremiseEncodingAccordions from './onPremiseEncodingAccordions';

//#region Styled Components

const StyledLeafletH2 = styled.h2`
  font-size: ${pxToEm(22)}em;
  color: ${baseVariables.colors.midnight};
`;

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 50%;
`;

const StyledTwoColumnsSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > ${StyledContentColumn} {
    flex: 1 1 50%;

    :nth-child(1) {
      border-right: 2px solid ${baseVariables.colors.darkBlueGray};
      padding-right: 5%;
    }

    :nth-child(2) {
      padding-left: 5%;
    }
  }
`;

const StyledHorizontalDivider = styled.div`
  background-color: ${baseVariables.colors.darkBlueGray};
  height: 2px;
`;

const StyledContentTwoColumnsSection = styled(StyledTwoColumnsSection)`
  padding-top: ${pxToEm(89)}em;
  padding-bottom: ${pxToEm(114)}em;
`;

const StyledH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  color: ${baseVariables.colors.midnight};
  display: inline-block;
  margin: 0 0 ${pxToEm(55) / pxToEm(24)}em;
`;

const StyledEncodingButtonsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${pxToEm(30)}em;
  margin-bottom: ${pxToEm(70)}em;

  > * {
    :not(:last-child) {
      margin-right: 1em;
    }
  }
`;

const StyledP = styled.p`
  font-size: ${pxToEm(20)}em;
  line-height: 1.6;
  margin: 0;
`;

const StyledImg = styled.img`
  width: 100%;
  margin-top: ${pxToEm(30)}em;
`;

const StyledMiddlePageCenterContainer = styled(StyledPageCenterContainer)`
  padding-top: 4rem;
  background-color: ${baseVariables.colors.lightBlueGray};
`;

//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {}

class OnPremiseEncodingPageView extends React.Component<Props> {
  public render() {
    return (
      <Layout>
        <PageViewHeadline
          topSubHeadline={ProductToText[Product.OnPremiseEncoding]}
          headline={'Deploy Everywhere'}
          badgeComponent={<HexagonIconImage iconSrc={ProductToHexagonIconSrc[Product.OnPremiseEncoding]} />}
          leftBadgeComponent={this.renderSeeItInActionButton()}
          rightBadgeComponent={
            <Link to={ProductToAppRoutePath[Product.Encoding]}>
              <HexagonLeaflet
                baseBg={HexagonBaseBg.Blue}
                direction={HexagonLeafletDirection.Left}
                hexagonIconSrc={HybridMultiCloudSvg}
                hexagonOverflowPx={28}>
                <StyledLeafletH2>{ProductToText[Product.Encoding]}</StyledLeafletH2>
              </HexagonLeaflet>
            </Link>
          }
        />

        <StyledHorizontalDivider />

        <StyledPageCenterContainer>
          <StyledContentTwoColumnsSection>
            <StyledContentColumn>
              <div>
                <StyledH2>Containerized video encoding on Kubernetes and Docker</StyledH2>
                <WrapperAppUl>
                  <li>Security Without Compromising Power</li>
                  <li>Cost Efficiency Through Resource Management</li>
                  <li>Unrivalled Performance, Control & Flexibility</li>
                </WrapperAppUl>
              </div>

              <StyledEncodingButtonsDiv>
                <BoundImageModal
                  boundComponent={<Button variant={ButtonVariant.OutlinedLight}>On-Premise Overview</Button>}
                  source={OnPremOverviewPng}
                />
                <BoundImageModal
                  boundComponent={<Button variant={ButtonVariant.OutlinedLight}>On-Premise Workflow</Button>}
                  source={OnPremWorkflowPng}
                />
                {/*tslint:disable:max-line-length*/}
                <a
                  href="https://bitmovin.com/dashboard/encoding/encodings?orgId=6af25605-1bd1-458d-8de9-1189f9ccf6df&3rdParty=false&apiKey=5cd462f7-73c1-424e-82d0-1a9bfabcf310"
                  target="_blank">
                  <Button variant={ButtonVariant.OutlinedLight}>Launch Dashboard</Button>
                </a>
                {/*tslint:enable:max-line-length*/}
              </StyledEncodingButtonsDiv>

              <OnPremiseEncodingAccordions />
            </StyledContentColumn>

            <StyledContentColumn>
              <StyledH2>The advantages of a flexible encoding stack</StyledH2>
              <StyledP>
                We know exactly how hard it can be to manage an inflexible hardware stack at scale in an ever changing
                market. That is why we built a flexible encoding stack with performance in mind.
              </StyledP>
              <BoundImageModal
                boundComponent={<StyledImg src={RunsAnywherePng} alt="RunsAnywherePng" />}
                source={RunsAnywherePng}
              />
            </StyledContentColumn>
          </StyledContentTwoColumnsSection>
        </StyledPageCenterContainer>

        <StyledMiddlePageCenterContainer>
          <WhyBitmovinProductSection
            product={Product.OnPremiseEncoding}
            leftBulletPoints={[
              {
                headline: 'Easy to integrate',
                description:
                  'Our solution can be deployed in any cloud or on-premise environment, ' +
                  'fully utilizing the infrastructure most suitable for the given use-case'
              },
              {
                headline: 'Runs everywhere',
                description:
                  'We can deploy our encoder into every public or private cloud, or any on-premise data center'
              }
            ]}
            rightBulletPoints={[
              {
                headline: 'Complex use-cases',
                description:
                  'We support hybrid use-cases in multiple scenarios ' +
                  '(private data warehouse, private or public cloud)'
              },
              {
                headline: 'Versatility',
                description: 'Customers can take full advantage of our solution in whichever way that suits them best'
              }
            ]}
          />
        </StyledMiddlePageCenterContainer>
      </Layout>
    );
  }

  private renderSeeItInActionButton = () => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === HardcodedSalesDemoPath.OnPremiseEncodingDemo);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <SeeItInActionButton to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})} hexagonIconSrc={StartDemoSvg} />
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(OnPremiseEncodingPageView);
